import { Row, Typography } from "antd";
import { t } from "i18next";
import React, { useCallback, useEffect, useState } from "react";
import applePayIcon from "../../assets/icons/applePay3.svg";
import "./index.less";

declare global {
  interface Window {
    paysafe: any; // Declare 'paysafe' as any type since it is loaded dynamically
  }
}

interface ApplePayButtonProps {
  paymentAmount: any;
  title: any;
  user: any;
  currentUser: any;
  handleApplePayPayment: any;
  type: any;
}

const ApplePayButton: React.FC<ApplePayButtonProps> = ({ paymentAmount, title, user, currentUser, handleApplePayPayment, type }) => {
  const [hasError, setHasError] = useState(false);
  const [instance, setInstance] = useState<any>(null);
  const isAndroidOrWindows = /android|windows/i.test(navigator.userAgent);
  const API_KEY = process.env.REACT_APP_PAY_SAFE_API_KEY;
  const ENVIRONMENT = process.env.REACT_APP_PAY_SAFE_ENVIRONMENT || "TEST";
  const ACCOUNT_ID = process.env.REACT_APP_PAY_SAFE_ACCOUNT_ID;
  const PAYSAFE_URL = process.env.REACT_APP_PAY_SAFE_URL;
  const MERCHANT_ID = process.env.REACT_APP_PAY_SAFE_MERCHANT_ID;
  const MANAGEMENT_URL = process.env.REACT_APP_PAY_SAFE_MANAGEMENT_URL;

  const generateGuid = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (crypto.getRandomValues(new Uint8Array(1))[0] & 15) >> 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const log = (message: any) => {
    const outputElement = document.getElementById("output");
    if (outputElement) {
      outputElement.innerHTML += `<br>[${new Date().toLocaleTimeString()}] ${JSON.stringify(message)}`;
    }
    // console.log(message);
  };

  const tokenize = useCallback(async () => {
    if (!instance) {
      log("Payment instance is not initialized.");
      return;
    }

    const twoDecimalAmount = Number(paymentAmount());
    const amount = Math.round(parseFloat(twoDecimalAmount.toFixed(2)) * 100);
    const label = title();

    log(`Amount to be charged: ${amount}`);

    const merchantRefNum = generateGuid();
    log(`Generated Merchant Reference Number: ${merchantRefNum}`);

    const tokenizationOptions =
      type === "subscription"
        ? {
            amount: amount,
            transactionType: "PAYMENT",
            paymentType: "APPLEPAY",
            merchantRefNum,
            applePay: {
              country: "US",
              requestBillingAddress: false,
              currencyCode: "EUR",
              merchantCapabilities: ["supports3DS", "supportsDebit", "supportsCredit"],
              supportedNetworks: ["visa", "masterCard", "amex", "discover"],
              recurringPaymentRequest: {
                paymentDescription: label,
                regularBilling: {
                  label: label,
                  amount,
                  paymentTiming: "recurring",
                },
                managementURL: MANAGEMENT_URL,
                tokenNotificationURL: `https://applepay-notifications.paysafe.com/v1/applepay/merchanttokens/${MERCHANT_ID}`,
              },
              total: {
                label: label,
                amount,
              },
            },
            customerDetails: {
              profile: {
                firstName: currentUser?.infos?.firstName, // These can be dynamic, assuming they are available
                lastName: currentUser?.infos?.lastName,
                email: currentUser?.infos?.privateInfos?.email,
              },
            },
          }
        : {
            amount: amount,
            transactionType: "PAYMENT",
            paymentType: "APPLEPAY",
            merchantRefNum,
            applePay: {
              country: "US",
              requestBillingAddress: true,
            },
            customerDetails: {
              profile: {
                firstName: currentUser?.infos?.firstName, // These can be dynamic, assuming they are available
                lastName: currentUser?.infos?.lastName,
                email: currentUser?.infos?.privateInfos?.email,
              },
            },
          };

    log("Starting tokenization process...");

    try {
      const result = await instance.tokenize(tokenizationOptions);
      log("Tokenization resolved.");
      log(`Token result ${JSON.stringify(result)}`);

      const billingContact = result?.applePay?.billingContact || {};
      const billingDetails = {
        street: billingContact.addressLines?.[0] || "",
        city: billingContact.locality || "",
        zip: billingContact.postalCode || "",
        country: billingContact.countryCode || "",
        state: billingContact.administrativeArea || "",
      };

      const paymentPayload = {
        merchantRefNum: tokenizationOptions.merchantRefNum,
        amount: tokenizationOptions.amount,
        currencyCode: "EUR",
        applePay: {
          applePayPaymentToken: result.token,
        },
        description: "Apple Pay Paysafe Js",
        billingDetails,
      };

      await logPost(`${PAYSAFE_URL}/paymenthub/v1/payments`, paymentPayload);
      instance.complete("success");
      handleApplePayPayment(result.token);
    } catch (error) {
      console.log("error --:>> ", error);
      log("Error on tokenization.");
      log(error);

      // We must close the Apple Pay window
      instance.complete("fail");
    }
  }, [instance, paymentAmount, user, currentUser]);

  const logPost = async (url: string, obj: any) => {
    const request = `curl --location --request POST '${url}' \
--header 'Authorization: Basic <(encode Base64) atlas_testing_private:(password from Geneva) >' \
--header 'Content-Type: application/json' \
--data-raw '${JSON.stringify(obj).replace(/\\"/g, '"')}'`;

    // const outputElement = document.getElementById("output");
    // if (outputElement) {
    //   let output = outputElement.innerHTML;
    //   output += `<br>[${getCurrentTime()}] ${request}`;
    //   outputElement.innerHTML = output;
    // }
  };

  const getCurrentTime = () => {
    const currentDate = new Date();
    return currentDate.toISOString().split("T")[1].split(".")[0]; // Better time format
  };

  const setupPaysafe = () => {
    if (isAndroidOrWindows) return;

    const label = title();
    log(`Setting up Paysafe with label: ${label}`);

    const options = {
      currencyCode: "EUR",
      environment: ENVIRONMENT,
      accounts: {
        default: Number(ACCOUNT_ID),
      },
      fields: {
        applePay: {
          selector: "#apple-pay",
          type: "buy",
          label: `${t(label)}`,
          color: "white-outline",
        },
      },
    };

    let mounted = true;
    window.paysafe?.fields
      .setup(API_KEY, options)
      .then((inst: any) => {
        if (!mounted) return;
        log("Paysafe instance successfully set up.");
        setInstance(inst);

        if (!inst.paymentMethods.applePay?.error) {
          log("Apple Pay is available.");
        } else {
          setHasError(true);
          log("Apple Pay not available.");
        }
      })
      .catch((error: any) => {
        setHasError(true);
        log(`Paysafe setup error: ${error}`);
      });

    return () => {
      mounted = false;
      setInstance(null);
      log("Component unmounted, instance reset.");
    };
  };
  useEffect(setupPaysafe, [isAndroidOrWindows, title, user?.infos?.name]);

  if (hasError) {
    console.log("hasError ======>", hasError);
    return <div></div>;
  }

  if (isAndroidOrWindows) {
    console.log("isAndroidOrWindows ======>", isAndroidOrWindows);
    return null; // Don't show on unsupported devices
  }

  return (
    <>
      <div
        id="apple-pay"
        style={{
          position: "absolute",
          zIndex: "-100",
          left: "-1000px",
        }}
      ></div>

      <button className="applePayBtn" onClick={tokenize}>
        <img src={applePayIcon} alt="Apple Pay" width="45px" />
      </button>

      <Row className="mt-20 mb-20 justify-content-center">
        <Typography.Text className="text-white-color font-18-bold">{t("auth-signUp-divider")}</Typography.Text>
      </Row>
    </>
  );
};

export default ApplePayButton;
